import { useEffect, useRef, useState } from "react";
import '../../styles/AdminStyles/RegistrationDetail.css';
import {
  EmailDelivery, EmailType, EMAIL_TYPE_LABELS, isAdminError, quotaResumesAt, Registration, RegistrationStatus, REGISTRATION_STATUS_LABELS, Webinar,
} from "../../webinars/types";
import { webinarRepository } from "../../webinars/repository";
import { deliveryLabel, deliveryTone } from "../../webinars/emailStatus";
import { formatDateTime, formatLongDate, formatResetTime, formatTime, formatTimeRange } from "../../webinars/format";
import { emailBody, emailLink, emailSubject, joiningMessage, whatsAppLink } from "../../webinars/messages";
import { copyText } from "../../webinars/clipboard";

interface RegistrationDetailProps {
  registration: Registration;
  /** Undefined if the webinar is no longer listed */
  webinar?: Webinar;
  /** The webinar's saved joining link, or "" */
  meetingLink: string;
  deliveries: Partial<Record<EmailType, EmailDelivery>>;
  onClose: () => void;
  notify: (message: string) => void;
}

const EMAIL_TYPES: EmailType[] = ["confirmation", "joining_instructions"];

export default function RegistrationDetail({ registration: r, webinar, meetingLink, deliveries, onClose, notify }: RegistrationDetailProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [statusError, setStatusError] = useState("");
  const [emailResult, setEmailResult] = useState("");
  const [sending, setSending] = useState(false);
  // The webinar's saved link is the starting point; it can be changed for this message only
  const [link, setLink] = useState(meetingLink);
  // null = follow the template; a string = the administrator has edited the message
  const [customMessage, setCustomMessage] = useState<string | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCloseRef.current(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const message = webinar ? customMessage ?? joiningMessage(r, webinar, link) : "";
  const whatsapp = webinar ? whatsAppLink(r.phone, message) : null;
  const email = webinar ? emailLink(r.email, emailSubject(webinar), emailBody(message)) : "";
  const linkChanged = link.trim() !== meetingLink;
  const joining = deliveries.joining_instructions;
  const canEmailInstructions = Boolean(webinar && meetingLink && r.status === "registered" && !r.archivedAt && webinar.status !== "cancelled");

  const setStatus = async (status: RegistrationStatus) => {
    setStatusError("");
    try {
      await webinarRepository.updateRegistration(r.id, { status });
      notify(`Marked ${r.firstName} ${r.lastName} as ${REGISTRATION_STATUS_LABELS[status].toLowerCase()}`);
    } catch (error) {
      setStatusError(isAdminError(error) ? error.message : "Couldn't update this registration.");
    }
  };

  const recordSent = async (sent: boolean) => {
    await webinarRepository.updateRegistration(r.id, { instructionsSentAt: sent ? new Date().toISOString() : null });
    notify(sent ? "Recorded as sent" : "Record cleared");
  };

  const saveLink = async () => {
    if (!webinar) return;
    try {
      await webinarRepository.saveMeetingLink(webinar.id, link);
      notify("Joining link saved to the webinar");
    } catch (error) {
      notify(isAdminError(error) ? error.message : "Couldn't save the link");
    }
  };

  const emailInstructions = async () => {
    if (!webinar) return;
    const again = deliveryTone(joining) !== "none";
    if (again && !window.confirm(`${r.firstName} already has joining instructions. Send them again?`)) return;
    setSending(true);
    setEmailResult("");
    try {
      const result = await webinarRepository.sendJoiningInstructions({ webinarId: webinar.id, registrationIds: [r.id], resend: again });
      setEmailResult(
        !result.emailEnabled ? "Queued: email sending isn't switched on yet."
          : result.failed ? "The email failed. See the error above and try again."
          : !result.sent && result.quota?.remaining === 0 ? `The email limit is reached, so it's waiting in the queue. It can be sent after the limit resets at ${formatResetTime(quotaResumesAt(result.quota))}.`
          : result.sent ? `Sent to ${r.email}.` : "Nothing was sent.");
    } catch (error) {
      setEmailResult(isAdminError(error) ? error.message : "Couldn't send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="wa-modal-backdrop" onClick={onClose}>
      <div className="wa-modal" role="dialog" aria-modal="true" aria-labelledby="wa-detail-title" onClick={e => e.stopPropagation()}>
        <div className="wa-modal-head">
          <div>
            <h2 id="wa-detail-title">{r.firstName} {r.lastName}</h2>
            <div className="wa-modal-chips">
              <span className="wa-ref-chip">{r.reference}</span>
              <span className={`wa-chip wa-chip--reg-${r.status}`}>{REGISTRATION_STATUS_LABELS[r.status]}</span>
              {r.archivedAt && <span className="wa-chip wa-chip--archived">Archived</span>}
            </div>
          </div>
          <button ref={closeRef} type="button" className="wa-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <dl className="wa-detail-list">
          <div><dt>Email</dt><dd><a href={`mailto:${r.email}`}>{r.email}</a></dd></div>
          <div><dt>Phone</dt><dd>{r.phone}</dd></div>
          <div><dt>Organisation</dt><dd>{r.organisation || "—"}</dd></div>
          <div><dt>Professional role</dt><dd>{r.professionalRole || "—"}</dd></div>
          <div>
            <dt>Webinar</dt>
            <dd>
              {!webinar ? "Unknown webinar" : r.roundStartAt ? (
                // An archived registration: the date of its own round, not the webinar's latest one
                <>{webinar.title}<small>{formatLongDate(r.roundStartAt)}, {formatTime(r.roundStartAt)} SAST (earlier round)</small></>
              ) : (
                <>{webinar.title}<small>{formatLongDate(webinar.startAt)}, {formatTimeRange(webinar.startAt, webinar.endAt)} SAST</small></>
              )}
            </dd>
          </div>
          <div><dt>Registered</dt><dd>{formatDateTime(r.registeredAt)} SAST</dd></div>
          {r.archivedAt && <div><dt>Archived</dt><dd>{formatDateTime(r.archivedAt)} SAST, when the webinar was reopened for a new round</dd></div>}
          <div><dt>Privacy notice</dt><dd>Accepted (version {r.privacyNoticeVersion})</dd></div>
          <div><dt>Marketing emails</dt><dd>{r.marketingConsent ? "Opted in" : "Not opted in"}</dd></div>
        </dl>

        <div className="wa-detail-section">
          <h3>Attendance</h3>
          <div className="wa-row-actions">
            {r.status === "registered" && (
              <>
                <button type="button" className="wa-btn" onClick={() => setStatus("attended")}>Mark attended</button>
                <button type="button" className="wa-btn wa-btn--danger" onClick={() => setStatus("cancelled")}>Mark cancelled</button>
              </>
            )}
            {r.status === "attended" && (
              <>
                <button type="button" className="wa-btn" onClick={() => setStatus("registered")}>Mark as registered</button>
                <button type="button" className="wa-btn wa-btn--danger" onClick={() => setStatus("cancelled")}>Mark cancelled</button>
              </>
            )}
            {r.status === "cancelled" && (
              <button type="button" className="wa-btn" onClick={() => setStatus("registered")}>Restore registration</button>
            )}
          </div>
          {statusError && <p className="wa-form-error" role="alert">{statusError}</p>}
        </div>

        <div className="wa-detail-section">
          <h3>Emails</h3>
          <ul className="wa-email-list">
            {EMAIL_TYPES.map(type => {
              const delivery = deliveries[type];
              return (
                <li key={type}>
                  <span className={`wa-mail wa-mail--${deliveryTone(delivery)}`}>{EMAIL_TYPE_LABELS[type]}: {deliveryLabel(delivery)}</span>
                  {delivery?.deliveredAt
                    ? <small>Delivered {formatDateTime(delivery.deliveredAt)} SAST</small>
                    : delivery?.sentAt && <small>{formatDateTime(delivery.sentAt)} SAST</small>}
                  {delivery?.complainedAt && <small className="wa-email-error">Marked as spam by the recipient {formatDateTime(delivery.complainedAt)} SAST</small>}
                  {delivery && delivery.attemptCount > 1 && <small>{delivery.attemptCount} attempts</small>}
                  {delivery?.lastError && deliveryTone(delivery) === "failed" && <small className="wa-email-error">{delivery.lastError}</small>}
                </li>
              );
            })}
          </ul>
          {canEmailInstructions ? (
            <div className="wa-row-actions">
              <button type="button" className="wa-btn" onClick={emailInstructions} disabled={sending}>
                {sending ? "Sending…" : deliveryTone(joining) === "none" ? "Email joining instructions" : "Email joining instructions again"}
              </button>
            </div>
          ) : (
            r.status === "registered" && !r.archivedAt && webinar && !meetingLink && (
              <p className="wa-muted">Save the joining link (Joining instructions, above the attendee list) to email it.</p>
            )
          )}
          {emailResult && <p className="wa-muted" role="status">{emailResult}</p>}
        </div>

        <div className="wa-detail-section">
          <h3>Send by hand (WhatsApp or your own email)</h3>
          {r.archivedAt ? (
            <p className="wa-muted">This registration is from an earlier round, so nothing is sent to it. If they want to attend the new round, they can register again.</p>
          ) : webinar?.status === "cancelled" ? (
            <p className="wa-warning">This webinar is cancelled, so joining instructions can't be sent.</p>
          ) : !webinar ? (
            <p className="wa-muted">This registration's webinar isn't listed, so there is nothing to send.</p>
          ) : (
            <>
              <label className="wa-field">
                Joining link <small>The webinar's saved link; change it here for this message only</small>
                <span className="wa-link-row">
                  <input type="url" placeholder="https://" value={link} onChange={e => setLink(e.target.value)} />
                  {linkChanged && link.trim() && (
                    <button type="button" className="wa-btn" onClick={saveLink}>Save to webinar</button>
                  )}
                </span>
              </label>
              {!link.trim() && (
                <p className="wa-warning">No joining link yet: the message will say “[Insert Webinar Link]”.</p>
              )}

              <label className="wa-field">
                Message
                <textarea rows={6} value={message} onChange={e => setCustomMessage(e.target.value)} />
              </label>
              {customMessage !== null && (
                <button type="button" className="wa-link" onClick={() => setCustomMessage(null)}>Reset to the standard message</button>
              )}

              <div className="wa-row-actions wa-compose-actions">
                {whatsapp ? (
                  <a className="wa-btn wa-btn--whatsapp" href={whatsapp} target="_blank" rel="noreferrer">Prepare WhatsApp message</a>
                ) : (
                  <span className="wa-btn wa-btn--disabled" title="This phone number isn't in a format WhatsApp accepts">Prepare WhatsApp message</span>
                )}
                <a className="wa-btn" href={email}>Prepare email</a>
                <button type="button" className="wa-btn" onClick={async () => { await copyText(message); notify("Message copied"); }}>Copy message</button>
                <button type="button" className="wa-btn" disabled={!link.trim()} onClick={async () => { await copyText(link.trim()); notify("Link copied"); }}>Copy link</button>
              </div>
              {!whatsapp && <p className="wa-muted">WhatsApp needs a number like 082 123 4567 or +27 82 123 4567.</p>}
              <p className="wa-muted">
                These open WhatsApp or your email app with the message filled in. Nothing is sent until you send it there yourself.
              </p>

              <div className="wa-sent-record">
                {r.instructionsSentAt ? (
                  <>
                    <span>✓ Recorded as sent by hand on {formatDateTime(r.instructionsSentAt)} SAST.</span>
                    <button type="button" className="wa-link" onClick={() => recordSent(false)}>Undo</button>
                  </>
                ) : (
                  <>
                    <span>Not recorded as sent by hand. Only record it once you have actually sent the message.</span>
                    <button type="button" className="wa-btn" onClick={() => recordSent(true)}>Record as sent</button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
